import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { FiCheck, FiFileText, FiPaperclip, FiTrash2 } from 'react-icons/fi';
import { SUB_STAGES, TODAY, type SubDoc } from './submissionData';
import {
  getNcToken, getToolboxToken, parseAmount, toDateInput, type SelectedOption,
} from '../../acordsmartscan/acordSmartScanShared';
import '../../commissionscan/CommissionScan.css';
import '../../../styles/AddQuotePage.css';

/**
 * The scan-to-prefill / manual-fields / note / attach-document block shared by
 * Add Quote (a quote on an existing opportunity) and step 3 of Add Opportunity
 * (an optional first quote on a brand-new one). Extracted so a future tweak —
 * button styling, layout, a new field — only has to happen once.
 *
 * Reports its current state upward on every change via `onChange`, rather than
 * an imperative handle, so a parent can both disable/enable its own submit
 * button live and read the final values when the user actually submits.
 */

export type DocType = 'DecPage' | 'QuoteLetter';

export interface QuoteFieldsValues {
  quoteId: string; stage: string; source: string; effectiveDate: string; expDate: string;
  carrier: string; mga: string; premium: string; note: string; docs: SubDoc[];
}

export interface QuoteFieldsResult { valid: boolean; fields: QuoteFieldsValues; }

export const todayISO = (): string => {
  const y = TODAY.getFullYear();
  const m = String(TODAY.getMonth() + 1).padStart(2, '0');
  const d = String(TODAY.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

interface Props {
  businessType: string;
  defaultEffective?: string | null;
  defaultExpiring?: string | null;
  onChange: (result: QuoteFieldsResult) => void;
}

const QuoteFieldsPanel: React.FC<Props> = ({ businessType, defaultEffective, defaultExpiring, onChange }) => {
  const [quoteId, setQuoteId] = useState('');
  const [stage, setStage] = useState('quoted');
  const [source, setSource] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(defaultEffective || '');
  const [expDate, setExpDate] = useState(defaultExpiring || '');
  const [carrier, setCarrier] = useState('');
  const [mga, setMga] = useState('');
  const [premium, setPremium] = useState('');
  const [note, setNote] = useState('');
  const [docs, setDocs] = useState<SubDoc[]>([]);

  const [documentType, setDocumentType] = useState<DocType>('DecPage');
  const [scanFile, setScanFile] = useState<File | null>(null);
  const [scanDragging, setScanDragging] = useState(false);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanError, setScanError] = useState('');
  const scanInputRef = useRef<HTMLInputElement>(null);

  const [attachDragging, setAttachDragging] = useState(false);
  const attachInputRef = useRef<HTMLInputElement>(null);

  const valid = !!(carrier.trim() && quoteId.trim() && stage && premium && effectiveDate);
  useEffect(() => {
    onChange({
      valid,
      fields: { quoteId, stage, source, effectiveDate, expDate, carrier, mga, premium, note, docs },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId, stage, source, effectiveDate, expDate, carrier, mga, premium, note, docs, valid]);

  const onScanDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setScanDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setScanFile(f);
  };
  const onScanFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) setScanFile(f);
    e.target.value = '';
  };

  const onScanAndPrefill = useCallback(async () => {
    if (!scanFile) return;
    setScanLoading(true);
    setScanError('');
    try {
      const data = new FormData();
      data.append('File', scanFile);
      data.append('documentType', documentType);
      data.append('ncToken', getNcToken());
      data.append('folderId', '');
      data.append('insuredId', '');
      data.append('isFileUploadFromAMS', 'false');
      data.append('fileName', scanFile.name);
      const selectedOption: SelectedOption = {
        SelectProspect: false, SelectInsured: false, CreateNewClient: false,
        UseExistingInsured: false, UseExistingProspect: false, CreateQuote: false,
        CreateNewPolicy: false, CreateNewProspect: false, ClientName: '',
        InsuredId: '', folderId: '', fileIds: [], isFileUploadFromAMSParam: false,
      };
      data.append('selectedOption', JSON.stringify(selectedOption));

      const res = await axios.post(`${process.env.REACT_APP_AcordScan_Url}/accord_scan`, data, {
        headers: { Authorization: `Bearer ${getToolboxToken()}` },
      });

      if (res.data.success === true) {
        const raw = typeof res.data.acordFormResponse === 'string'
          ? JSON.parse(res.data.acordFormResponse)
          : (res.data.acordFormResponse || res.data);

        if (raw.carrierName) setCarrier(raw.carrierName);
        if (raw.policyPremium) setPremium(String(parseAmount(raw.policyPremium)));
        if (raw.effectiveDate) setEffectiveDate(toDateInput(raw.effectiveDate));
        if (raw.expirationDate) setExpDate(toDateInput(raw.expirationDate));
        const idFromScan = raw.quoteNumber || raw.policyNumber;
        if (idFromScan) setQuoteId(idFromScan);

        setDocs(prev => [...prev, {
          name: scanFile.name,
          kind: documentType === 'DecPage' ? 'Dec Page' : 'Quote Letter',
          ok: true,
          note: 'Auto-attached from scan',
          when: todayISO(),
        }]);
        setScanFile(null);
      } else {
        setScanError(res.data.errorMessage || 'Could not extract details from this document.');
      }
    } catch (err: any) {
      setScanError(err?.response?.data?.errorMessage || 'Scan failed — check the file and try again.');
    } finally {
      setScanLoading(false);
    }
  }, [scanFile, documentType]);

  const addDocsFromFiles = (files: File[]) => {
    if (!files.length) return;
    setDocs(prev => [...prev, ...files.map(f => ({
      name: f.name, kind: 'Attachment', ok: true, note: 'Attached manually', when: todayISO(),
    }))]);
  };
  const onAttachDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setAttachDragging(false);
    addDocsFromFiles(Array.from(e.dataTransfer.files || []));
  };
  const onAttachFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    addDocsFromFiles(Array.from(e.target.files || []));
    e.target.value = '';
  };
  const removeDoc = (idx: number) => setDocs(prev => prev.filter((_, i) => i !== idx));

  return (
    <>
      <section className="sc-panel__section aq-section">
        <h3 className="sc-panel__section-title">Scan a dec page or quote letter to prefill</h3>
        <div className="cs-tabs">
          <button
            type="button"
            className={`cs-tab${documentType === 'DecPage' ? ' cs-tab--active' : ''}`}
            onClick={() => setDocumentType('DecPage')}
          >
            Dec Page
          </button>
          <button
            type="button"
            className={`cs-tab${documentType === 'QuoteLetter' ? ' cs-tab--active' : ''}`}
            onClick={() => setDocumentType('QuoteLetter')}
          >
            Quote Letter
          </button>
        </div>
        <div
          className={`cs-dropzone aq-dropzone${scanDragging ? ' cs-dropzone--dragging' : ''}`}
          onDrop={onScanDrop}
          onDragOver={(e) => { e.preventDefault(); setScanDragging(true); }}
          onDragLeave={() => setScanDragging(false)}
        >
          {!scanFile ? (
            <>
              <div className="cs-dropzone__icon"><FiFileText size={22} /></div>
              <p className="cs-dropzone__title">
                Drag &amp; drop {documentType === 'DecPage' ? 'a dec page' : 'a quote letter'} PDF here
              </p>
              <p className="cs-dropzone__or">or</p>
              <button type="button" className="cs-browse-btn cs-browse-btn--sm" onClick={() => scanInputRef.current?.click()}>
                Browse Files
              </button>
              <input ref={scanInputRef} type="file" accept=".pdf" style={{ display: 'none' }} onChange={onScanFileInput} />
            </>
          ) : (
            <div className="cs-file-card">
              <div className="cs-file-card__icon"><FiFileText size={18} /></div>
              <div className="cs-file-card__info">
                <span className="cs-file-card__name">{scanFile.name}</span>
                <span className="cs-file-card__size">{(scanFile.size / 1024).toFixed(0)} KB</span>
              </div>
              <button type="button" className="cs-file-card__delete" onClick={() => setScanFile(null)} title="Remove">
                <FiTrash2 size={14} />
              </button>
            </div>
          )}
        </div>
        {scanError && <p className="aq-error">{scanError}</p>}
        <button
          type="button"
          className="cs-browse-btn cs-browse-btn--sm aq-scan-btn"
          disabled={!scanFile || scanLoading}
          onClick={onScanAndPrefill}
        >
          {scanLoading ? 'Scanning…' : 'Scan & prefill'}
        </button>
      </section>

      <section className="sc-panel__section aq-section">
        <h3 className="sc-panel__section-title">Quote details</h3>
        <div className="aq-grid">
          <div className="aq-field">
            <label className="aq-label">Quote Id *</label>
            <input className="glass-input" value={quoteId} onChange={(e) => setQuoteId(e.target.value)} placeholder="e.g. SUB-882301" />
          </div>
          <div className="aq-field">
            <label className="aq-label">Stage *</label>
            <select className="glass-input" value={stage} onChange={(e) => setStage(e.target.value)}>
              {SUB_STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          <div className="aq-field">
            <label className="aq-label">Source</label>
            <input
              className="glass-input"
              list="aq-source-options"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              placeholder="e.g. Direct"
            />
            <datalist id="aq-source-options">
              <option value="Direct appointment" />
              <option value="via Rate" />
              <option value="Wholesale / MGA" />
            </datalist>
          </div>
          <div className="aq-field">
            <label className="aq-label">Business type</label>
            <input className="glass-input" value={businessType} disabled readOnly />
          </div>
          <div className="aq-field">
            <label className="aq-label">Effective date *</label>
            <input type="date" className="glass-input" value={effectiveDate || ''} onChange={(e) => setEffectiveDate(e.target.value)} />
          </div>
          <div className="aq-field">
            <label className="aq-label">Exp date</label>
            <input type="date" className="glass-input" value={expDate || ''} onChange={(e) => setExpDate(e.target.value)} />
          </div>
          <div className="aq-field">
            <label className="aq-label">Carrier *</label>
            <input className="glass-input" value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="e.g. Travelers" />
          </div>
          <div className="aq-field">
            <label className="aq-label">MGA <span className="aq-optional">(optional)</span></label>
            <input className="glass-input" value={mga} onChange={(e) => setMga(e.target.value)} placeholder="e.g. First Connect" />
          </div>
          <div className="aq-field">
            <label className="aq-label">Premium *</label>
            <input type="number" min="0" className="glass-input" value={premium} onChange={(e) => setPremium(e.target.value)} placeholder="e.g. 171450" />
          </div>
        </div>
      </section>

      <section className="sc-panel__section aq-section">
        <h3 className="sc-panel__section-title">Note</h3>
        <textarea
          className="glass-input aq-note"
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add a note about this quote…"
        />
      </section>

      <section className="sc-panel__section aq-section">
        <h3 className="sc-panel__section-title">Documents</h3>
        {docs.length === 0 ? (
          <p className="sc-panel__note">No documents attached yet.</p>
        ) : (
          <ul className="sc-panel__list">
            {docs.map((d, i) => (
              <li key={`${d.name}-${i}`}>
                <span className="sc-panel__list-icon"><FiCheck size={12} /></span>
                <span className="sc-panel__list-main">
                  {d.name}
                  <span className="sc-panel__list-meta">{d.kind} · {d.note}</span>
                </span>
                <button type="button" className="aq-doc-remove" onClick={() => removeDoc(i)} aria-label={`Remove ${d.name}`}>
                  <FiTrash2 size={13} />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div
          className={`aq-attach${attachDragging ? ' aq-attach--dragging' : ''}`}
          onDrop={onAttachDrop}
          onDragOver={(e) => { e.preventDefault(); setAttachDragging(true); }}
          onDragLeave={() => setAttachDragging(false)}
        >
          <button type="button" className="cs-browse-btn cs-browse-btn--sm" onClick={() => attachInputRef.current?.click()}>
            <FiPaperclip size={13} /> Attach documents
          </button>
          <span className="aq-attach__hint">or drag files here — attaches without scanning. You can attach as many as you need.</span>
          <input ref={attachInputRef} type="file" multiple style={{ display: 'none' }} onChange={onAttachFileInput} />
        </div>
      </section>
    </>
  );
};

export default QuoteFieldsPanel;
